/**
 * Security utilities for BIRHAUS components
 * 
 * Provides safe object access patterns to prevent object injection vulnerabilities
 * while maintaining type safety and performance.
 */

/**
 * Type guard to validate that a key exists in an object
 */
export function validateObjectKey<T extends Record<string, unknown>>(
  key: string, 
  obj: T
): key is Extract<keyof T, string> {
  return key in obj && Object.prototype.hasOwnProperty.call(obj, key)
}

/**
 * Safe object property access with fallback
 */
export function safeAccess<T extends Record<string, unknown>, F>(
  obj: T,
  key: string,
  fallback: F
): T[Extract<keyof T, string>] | F {
  if (validateObjectKey(key, obj)) {
    // eslint-disable-next-line security/detect-object-injection -- Safe access after validation
    return obj[key] as T[Extract<keyof T, string>]
  }
  return fallback
}

/**
 * Safe array access with bounds checking
 */
export function safeArrayAccess<T>(
  array: T[],
  index: number,
  fallback: T
): T {
  if (index >= 0 && index < array.length) {
    // eslint-disable-next-line security/detect-object-injection -- Safe array access with bounds check
    return array[index] as T
  }
  return fallback
}

/**
 * Validate variant against allowed values with type safety
 */
export function validateVariant<T extends string>(
  variant: string,
  allowedVariants: readonly T[],
  fallback: T
): T {
  return allowedVariants.includes(variant as T) ? variant as T : fallback
}

/**
 * Safe CSS class lookup with validation
 * Common pattern for component styling
 */
export function safeClassLookup<T extends Record<string, string>>(
  classes: T,
  key: string,
  fallback: string
): string {
  if (validateObjectKey(key, classes)) {
    // eslint-disable-next-line security/detect-object-injection -- Safe access after validation
    return classes[key] as string
  }
  return fallback
}

/**
 * Common BIRHAUS component variants for reuse
 */
export const BIRHAUS_VARIANTS = ['default', 'primary', 'secondary', 'success', 'warning', 'error', 'info'] as const
export type BirhausVariant = typeof BIRHAUS_VARIANTS[number]

/**
 * Common BIRHAUS component sizes for reuse
 */
export const BIRHAUS_SIZES = ['sm', 'md', 'lg', 'xl'] as const
export type BirhausSize = typeof BIRHAUS_SIZES[number]

/**
 * Validate BIRHAUS variant with type safety
 */
export function validateBirhausVariant(variant: string): BirhausVariant {
  return validateVariant(variant, BIRHAUS_VARIANTS, 'default')
}

/**
 * Validate BIRHAUS size with type safety
 */
export function validateBirhausSize(size: string): BirhausSize {
  return validateVariant(size, BIRHAUS_SIZES, 'md')
}

/**
 * Safe theme color access
 * Prevents theme object injection vulnerabilities
 */
export function safeThemeAccess<T extends Record<string, string>>(
  theme: T,
  path: string,
  fallback: string
): string {
  return safeAccess(theme, path, fallback as T[keyof T]) as string
}